import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * Il menu laterale (03-SCHERMATE-TV §2): 280 px, alto tutto lo schermo.
 *
 * ## Perché le voci sono meno di sette
 *
 * Il disegno ne elenca sette — `Cerca`, `Home`, `Guida TV`, `Film`, `Serie TV`,
 * `Canali preferiti`, `Impostazioni` — e quattro appartengono alla fase 4: non esistono ancora.
 *
 * Qui ci sono solo quelle che funzionano, e non è una semplificazione: **una voce che c'è e non
 * funziona è peggio di una che manca.** Chi la preme e non ottiene niente smette di fidarsi del
 * menu intero, e la prossima volta non prova nemmeno quella che avrebbe funzionato. È la stessa
 * regola con cui il pannello nasconde le sezioni senza licenza invece di ingrigirle.
 *
 * Le altre si aggiungono quando c'è qualcosa dietro, e l'ordine del disegno si rispetta lo stesso:
 * le voci che arriveranno si infilano al loro posto, non in coda.
 */

/** La larghezza del disegno (03-SCHERMATE-TV §2). Esportata perché chi disegna accanto deve saperla. */
export const SIDE_MENU_WIDTH_PX = 280;

export interface MenuEntry {
  readonly id: string;
  /** La chiave i18n del nome mostrato. */
  readonly labelKey: string;
}

export interface SideMenuProps {
  readonly focusPort: FocusPort;
  readonly entries: ReadonlyArray<MenuEntry>;
  /** Quale sezione si sta guardando: resta acceso anche entrando nei suoi sottolivelli. */
  readonly activeId: string | null;
  readonly onChoose: (id: string) => void;
  readonly onDismiss: () => void;
  /**
   * Il nome del profilo, in fondo alla colonna come nel prototipo.
   *
   * **`null` quando non se ne conosce uno**, e allora il blocco non si disegna. Il prototipo scrive
   * «Marco», che è dato finto: l'app un nome vero lo ha soltanto con l'accesso a utente e password —
   * con il gettone la linea non ne dichiara nessuno, e `LoginResult` non porta né profilo né
   * anagrafica. Inventare «Utente» o «Profilo 1» riempirebbe lo spazio del disegno con una cosa che
   * non viene da nessun dato: chi legge crederebbe che l'app sappia chi è.
   *
   * La seconda riga — «Profilo principale» — è invece un'etichetta fissa e sta nell'i18n: i profili
   * multipli non esistono ancora, e quando esisteranno quella riga dirà quale è in uso.
   */
  readonly profileName?: string | null;
  /**
   * Uscire dall'account. Nel prototipo è un tasto **sotto** il profilo, staccato dalle voci: non è
   * una sezione dove si va, è un'azione che si compie — e va dove sta la persona a cui si riferisce.
   */
  readonly onSignOut: () => void;
  /**
   * Rilegge il catalogo dal gateway, saltando l'archivio locale.
   *
   * Serve perché il catalogo ora vive sull'apparecchio: una modifica fatta dal pannello — un canale
   * aggiunto, il catch-up acceso — non si vede finché non scade, e la scadenza è in ore d'uso. Senza
   * questo tasto l'unico modo di forzare sarebbe uscire e rientrare, cioè ridigitare le credenziali
   * per un aggiornamento.
   *
   * Sta accanto a «Esci» perché sono le due azioni della colonna, non due sezioni dove si va.
   */
  readonly onRefresh: () => void;
  /** Vero mentre il catalogo si sta rileggendo: il tasto lo dice invece di sembrare morto. */
  readonly refreshing?: boolean;
  /**
   * Se il menu ha **il fuoco**, cioè se i tasti sono suoi.
   *
   * Visibile e a fuoco sono cose diverse, e confonderle costa caro: il menu sta sullo schermo anche
   * mentre si usa la guida, ma se si iscrivesse comunque al telecomando la stessa freccia
   * arriverebbe a tutti e due — il cursore della guida si muoverebbe **e** si muoverebbe la voce
   * del menu. Una prova conta gli ascoltatori che consumano un tasto e pretende che sia uno.
   */
  readonly focused: boolean;
}

export function SideMenu({
  focusPort,
  entries,
  activeId,
  onChoose,
  onDismiss,
  focused,
  profileName = null,
  onSignOut,
  onRefresh,
  refreshing = false,
}: SideMenuProps): ReactNode {
  const translator = useTranslator();

  /*
    «Esci» è l'ultima posizione raggiungibile, dopo le voci.

    Non è una voce dell'elenco — nel prototipo sta sotto il profilo, con un disegno suo — ma **è**
    raggiungibile col telecomando, quindi per il cursore è un indice come gli altri. Tenerlo fuori dal
    conteggio lo renderebbe visibile e non premibile, che è il difetto peggiore di tutti: si vede
    qualcosa e il telecomando non ci arriva.
  */
  const refreshIndex = entries.length;
  const signOutIndex = entries.length + 1;

  /*
    Il fuoco parte dalla voce attiva, non dalla prima.

    Aprire il menu è «dove sono, e dove altro posso andare»: partire dall'alto costringerebbe a
    ritrovare ogni volta il punto in cui si era, ed è la stessa ragione per cui la guida si apre sul
    canale che si sta guardando.
  */
  const [cursor, setCursor] = useState(() => {
    const at = entries.findIndex((entry) => entry.id === activeId);
    return at >= 0 ? at : 0;
  });

  const onChooseRef = useRef(onChoose);
  const onDismissRef = useRef(onDismiss);
  const onSignOutRef = useRef(onSignOut);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onChooseRef.current = onChoose;
    onDismissRef.current = onDismiss;
    onSignOutRef.current = onSignOut;
    onRefreshRef.current = onRefresh;
  }, [onChoose, onDismiss, onSignOut, onRefresh]);

  const move = useCallback(
    (delta: number) => {
      setCursor((current) => {
        const next = current + delta;
        // Ai bordi si resta: un menu di quattro voci che avvolge fa premere due volte per tornare
        // dove si era, e su un telecomando si sente.
        return next < 0 || next > signOutIndex ? current : next;
      });
    },
    [signOutIndex],
  );

  useEffect(() => {
    if (!focused) return undefined;
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        onDismissRef.current();
        return true;
      }
      if (key.kind === 'select') {
        if (cursor === refreshIndex) {
          onRefreshRef.current();
          return true;
        }
        if (cursor === signOutIndex) {
          onSignOutRef.current();
          return true;
        }
        const entry = entries[cursor];
        if (entry) onChooseRef.current(entry.id);
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'up') move(-1);
      if (key.direction === 'down') move(1);
      // Destra esce dal menu verso l'area principale, come su ogni menu laterale. Sinistra no:
      // non c'è niente a sinistra, e consumarla farebbe sembrare il tasto rotto.
      if (key.direction === 'right') onDismissRef.current();
      return key.direction !== 'left';
    });
  }, [focusPort, entries, cursor, move, focused, refreshIndex, signOutIndex]);

  return (
    <nav
      data-side-menu="1"
      style={{
        /*
          Una **colonna**, non una sovrapposizione.

          Da sovrapposto bisognava spostare ogni strato con un `left`, e bastava che uno strato
          interno fosse posizionato rispetto a un altro contenitore per finirgli sotto: è successo
          con la colonna dei canali della guida, che si disegnava dietro le voci del menu. Occupando
          lo spazio, il problema non esiste — nessuno deve ricordarsi di spostare niente.
        */
        flex: 'none',
        width: SIDE_MENU_WIDTH_PX,
        alignSelf: 'stretch',
        // I valori sono quelli del prototipo, non scelti a occhio: fondo proprio della colonna
        // (più scuro dello sfondo), bordo appena accennato, respiro in alto.
        background: colors.railBackground,
        borderRight: `1px solid ${colors.borderFaint}`,
        padding: '56px 24px 48px',
        opacity: focused ? 1 : 0.62,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxSizing: 'border-box',
      }}
    >
      {/* Il marchio, come nel prototipo: il nome e il punto ambra. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 5,
          padding: '0 20px 40px',
          font: `800 46px/1 ${FONT_STACK}`,
          letterSpacing: '-.03em',
        }}
      >
        palco
        <span
          style={{ width: 13, height: 13, borderRadius: 7, background: colors.accent, display: 'inline-block' }}
        />
      </div>

      {entries.map((entry, index) => {
        const isFocused = focused && index === cursor;
        const active = entry.id === activeId;
        return (
          <button
            key={entry.id}
            type="button"
            data-menu-entry={entry.id}
            data-focused={index === cursor ? '1' : '0'}
            data-active={active ? '1' : '0'}
            onClick={() => onChooseRef.current(entry.id)}
            style={{
              // Misure del prototipo, non scelte a occhio: alta 68, angoli 12, testo 26.
              height: 68,
              padding: '0 20px',
              borderRadius: 12,
              border: 0,
              margin: 0,
              textAlign: 'left',
              // Attiva e a fuoco sono cose diverse: «dove sono» e «cosa sto per premere». Un menu
              // che le confonde risponde alla domanda sbagliata.
              background: active ? colors.surfaceRaised : 'transparent',
              color: active ? colors.accent : colors.text,
              font: `600 26px ${FONT_STACK}`,
              cursor: 'pointer',
              /*
                Il fuoco è un **contorno ambra con ingrandimento**, non il bordo a sinistra che avevo
                inventato io.

                Su un televisore a tre metri un bordo di quattro pixel non si vede. Il contorno con
                lo stacco e la scala sono ciò che fa capire dove si è da un divano, ed è per questo
                che il prototipo li usa.
              */
              outline: isFocused ? `4px solid ${colors.accent}` : 'none',
              outlineOffset: isFocused ? 4 : 0,
              transform: isFocused ? 'scale(1.04)' : 'none',
              transition: 'transform .16s ease',
            }}
          >
            {translator.t(entry.labelKey)}
          </button>
        );
      })}

      {/*
        Il blocco del profilo, spinto in fondo dal `marginTop: auto`.

        Nel prototipo sta sotto le voci e sopra «Esci»: non è raggiungibile col telecomando perché non
        è un'azione — dice **chi sta guardando**, e serve su un apparecchio di casa dove il
        telecomando passa di mano.
      */}
      {profileName !== null && profileName.length > 0 && (
        <div
          data-side-menu-profile="1"
          style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px' }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 26,
              background: colors.avatarBackground,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: `700 24px ${FONT_STACK}`,
              flex: 'none',
            }}
          >
            {/* L'iniziale del nome vero, non una lettera scelta da noi. */}
            {profileName.trim().charAt(0).toUpperCase()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span
              style={{
                font: `600 22px ${FONT_STACK}`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {profileName}
            </span>
            <span style={{ font: `400 17px ${FONT_STACK}`, color: colors.textDim }}>
              {translator.t('menu.mainProfile')}
            </span>
          </div>
        </div>
      )}

      {/*
        «Aggiorna il catalogo», sopra «Esci».

        Sono le due **azioni** della colonna — non sezioni dove si va — e per questo stanno staccate
        dalle voci e con un disegno loro.
      */}
      <button
        type="button"
        data-f="1"
        data-menu-refresh="1"
        data-focused={cursor === refreshIndex ? '1' : '0'}
        disabled={refreshing}
        onClick={() => onRefreshRef.current()}
        style={{
          marginTop: profileName !== null && profileName.length > 0 ? 10 : 'auto',
          height: 56,
          padding: '0 20px',
          border: 0,
          borderRadius: 12,
          textAlign: 'left',
          cursor: refreshing ? 'default' : 'pointer',
          background: focused && cursor === refreshIndex ? colors.surfaceRaised : 'transparent',
          color: refreshing ? colors.textFaint : focused && cursor === refreshIndex ? colors.text : colors.textDim,
          font: `600 22px ${FONT_STACK}`,
          outline: focused && cursor === refreshIndex ? `4px solid ${colors.accent}` : 'none',
          outlineOffset: 2,
        }}
      >
        {/* Mentre sta leggendo lo dice: un tasto che non risponde sembra rotto, e si ripreme. */}
        {translator.t(refreshing ? 'menu.refreshing' : 'menu.refresh')}
      </button>

      {/*
        «Esci», staccato dalle voci e con un fuoco diverso: il prototipo qui **non** ingrandisce e non
        scosta di 4 px — accende un fondo. È l'unica azione irreversibile della colonna, e il disegno
        la distingue invece di farla sembrare una sezione in più.
      */}
      <button
        type="button"
        data-f="1"
        data-menu-sign-out="1"
        data-focused={cursor === signOutIndex ? '1' : '0'}
        onClick={() => onSignOutRef.current()}
        style={{
          // Il `marginTop: auto` ce l'ha ormai il tasto sopra: qui basta lo stacco.
          marginTop: 4,
          height: 56,
          padding: '0 20px',
          border: 0,
          borderRadius: 12,
          textAlign: 'left',
          cursor: 'pointer',
          background: focused && cursor === signOutIndex ? colors.surfaceRaised : 'transparent',
          color: focused && cursor === signOutIndex ? colors.text : colors.textDim,
          font: `600 22px ${FONT_STACK}`,
          outline: focused && cursor === signOutIndex ? `4px solid ${colors.accent}` : 'none',
          outlineOffset: 2,
        }}
      >
        {translator.t('menu.signOut')}
      </button>
    </nav>
  );
}
